import { useState } from "react";
import { Edit, KeyRound, MoreHorizontal } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { UserWithProfile } from "@/types/user-profile";
import { EvaluationWeightInput } from "./EvaluationWeightInput";
import { ResetPasswordModal } from "./ResetPasswordModal";
import { UserKind } from "./user-kind";

interface UsersTableProps {
  users: UserWithProfile[];
  kind: UserKind;
  onChangeProfile: (user: UserWithProfile) => void;
}

function displayName(user: UserWithProfile) {
  return user.full_name || user.username || "";
}

export function UsersTable({ users, kind, onChangeProfile }: UsersTableProps) {
  const [resetPasswordUser, setResetPasswordUser] = useState<UserWithProfile | null>(null);
  const showAgency = kind === UserKind.Agency;
  const sortedUsers = [...users].sort((first, second) =>
    displayName(first).localeCompare(displayName(second), "pt-BR"),
  );

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>E-mail</TableHead>
              <TableHead>Perfil</TableHead>
              {showAgency && <TableHead>Agência</TableHead>}
              <TableHead className="text-right">Peso</TableHead>
              <TableHead className="text-right">
                <span className="sr-only">Ações</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="font-medium">{displayName(user) || "Sem nome"}</TableCell>
                <TableCell className="text-muted-foreground">{user.email}</TableCell>
                <TableCell>
                  {user.access_profile_name ? (
                    <Badge variant="default">{user.access_profile_name}</Badge>
                  ) : (
                    <Badge variant="secondary">Sem perfil</Badge>
                  )}
                </TableCell>
                {showAgency && <TableCell>{user.agencia_nome}</TableCell>}
                <TableCell className="text-right">
                  <EvaluationWeightInput key={`${user.id}-${user.evaluation_weight}`} user={user} />
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Ações">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => setResetPasswordUser(user)}>
                        <KeyRound className="mr-2 h-4 w-4" />
                        Redefinir senha
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => onChangeProfile(user)}>
                        <Edit className="mr-2 h-4 w-4" />
                        Alterar perfil
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {resetPasswordUser && (
        <ResetPasswordModal
          user={resetPasswordUser}
          open
          onOpenChange={(open) => {
            if (!open) setResetPasswordUser(null);
          }}
        />
      )}
    </>
  );
}
